<template>
  <!-- Payment Details Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Premium Payment Details</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <!-- Loading State for Modal -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading payment details...</p>
          </div>

          <!-- Payment Details Content -->
          <div v-else-if="paymentData">
            <!-- Policy Holder Info -->
            <div class="payment-detail-card payment-status-success rounded p-4 mb-4 border-success border badge-soft-success">
              <div class="row">
                <div class="col-md-8">
                  <div class="d-flex mb-3">
                    <div class="avatar-sm">
                      <span class="avatar-title rounded-circle bg-primary text-white">
                        {{ getInitials(paymentData.first_name, paymentData.surname) }}
                      </span>
                    </div>
                    <div class="mx-3">
                      <h4 class="mb-1">{{ paymentData.first_name || 'N/A' }} {{ paymentData.surname || 'N/A' }}</h4>
                      <p class="text-muted mb-1">CBHI Number: <strong>{{ paymentData.cbhi_number || 'N/A' }}</strong></p>
                      <p class="text-muted mb-0">
                        <i class="bx bx-map me-1"></i>{{ paymentData.zone_name || 'N/A' }}, {{ paymentData.woreda_name || 'N/A' }}, {{ paymentData.kebele_name || 'N/A' }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-md-end">
                  <div class="mb-2">
                    <span :class="getStatusBadge(paymentData.status) + ' fs-6 px-3 py-2'">
                      <i :class="getStatusIcon(paymentData.status)" class="me-1"></i>{{ paymentData.status.toUpperCase() }}
                    </span>
                  </div>
                  <div class="amount-highlight text-success">
                    <h1 class="fw-semibold display-6 mb-0 text-success">ETB {{ formatAmount(paymentData.amount) }}</h1>
                  </div>
                  <p class="text-muted">Payment Amount</p>
                </div>
              </div>
            </div>

            <!-- Payment Information -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card h-100 border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="bx bx-info-circle me-1"></i>Payment Information</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-borderless table-sm">
                      <tr>
                        <td class="text-muted">Payment Date:</td>
                        <td>{{ formatDate(paymentData.payment_date) }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Payment Year:</td>
                        <td>{{ paymentData.payment_year }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Payment Method:</td>
                        <td>
                          <span :class="getPaymentMethodBadge(paymentData.method_name)">
                            {{ paymentData.method_name }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Reference Number:</td>
                        <td>{{ paymentData.payment_reference }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Receipt Number:</td>
                        <td>{{ paymentData.receipt_number || '-' }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card h-100 border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="bx bx-user me-1"></i>Collection Details</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-borderless table-sm">
                      <tr>
                        <td class="text-muted">Collected By:</td>
                        <td>{{ paymentData.collected_by }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Collection Point:</td>
                        <td>{{ paymentData.collection_point }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Bank Reference:</td>
                        <td>{{ paymentData.bank_reference || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Notes:</td>
                        <td>{{ paymentData.notes || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Created At:</td>
                        <td>{{ formatDateTime(paymentData.created_at) }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Agency Banking Information (if CASH payment) -->
            <div v-if="paymentData.method_name === 'Cash'" class="row mb-4">
              <div class="col-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">
                      <i class="bx bx-transfer me-1"></i>Agency Banking Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tr>
                            <td class="text-muted">Agency Status:</td>
                            <td>
                              <span :class="getAgencyBankingStatusBadge(paymentData.agency_banking_status)">
                                <i :class="getAgencyBankingStatusIcon(paymentData.agency_banking_status)" class="me-1"></i>
                                {{ capitalizeStatus(paymentData.agency_banking_status || 'not_sent') }}
                              </span>
                            </td>
                          </tr>
                          <tr v-if="paymentData.agency_banking_reference">
                            <td class="text-muted">Agency Reference:</td>
                            <td><strong>{{ paymentData.agency_banking_reference }}</strong></td>
                          </tr>
                          <tr v-if="paymentData.agency_banking_receipt">
                            <td class="text-muted">Agency Receipt:</td>
                            <td><strong>{{ paymentData.agency_banking_receipt }}</strong></td>
                          </tr>
                          <tr v-if="paymentData.agency_banking_transaction_id">
                            <td class="text-muted">Transaction ID:</td>
                            <td>{{ paymentData.agency_banking_transaction_id }}</td>
                          </tr>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tr v-if="paymentData.agency_banking_sent_at">
                            <td class="text-muted">Sent At:</td>
                            <td>{{ formatDateTime(paymentData.agency_banking_sent_at) }}</td>
                          </tr>
                          <tr v-if="paymentData.agency_banking_retry_count">
                            <td class="text-muted">Retry Count:</td>
                            <td>{{ paymentData.agency_banking_retry_count }}</td>
                          </tr>
                          <tr v-if="paymentData.agency_banking_error">
                            <td class="text-muted">Error:</td>
                            <td class="text-danger">{{ paymentData.agency_banking_error }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    <!-- Retry Button for Failed Agency Banking -->
                    <div v-if="paymentData.agency_banking_status === 'failed' && (paymentData.agency_banking_retry_count || 0) < 3" class="mt-3">
                      <button @click="retryAgencyBanking"
                              :disabled="retryLoading"
                              class="btn btn-warning btn-sm">
                        <span v-if="retryLoading" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="bx bx-refresh me-1"></i>
                        Retry Agency Banking
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plan Information -->
            <div v-if="planData" class="row mb-4">
              <div class="col-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="bx bx-shield me-1"></i>Plan Information</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tr>
                            <td class="text-muted">Plan Name:</td>
                            <td><strong>{{ planData.plan_name }}</strong></td>
                          </tr>
                          <tr>
                            <td class="text-muted">Plan Code:</td>
                            <td>{{ planData.plan_code }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Plan Type:</td>
                            <td>{{ capitalizeStatus(planData.plan_type) }}</td>
                          </tr>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tr>
                            <td class="text-muted">Annual Premium:</td>
                            <td>ETB {{ formatAmount(planData.primary_annual_amount) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Coverage Limit:</td>
                            <td>ETB {{ formatAmount(planData.annual_coverage_limit) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Per Visit Limit:</td>
                            <td>ETB {{ formatAmount(planData.per_visit_limit) }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Summary -->
            <div v-if="paymentSummary" class="row mb-4">
              <div class="col-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="bx bx-calculator me-1"></i>Payment Summary for {{ paymentData.payment_year }}</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3 col-6 text-center">
                        <h5 class="text-primary">ETB {{ formatAmount(paymentSummary.expected_annual_premium) }}</h5>
                        <p class="text-muted mb-0">Expected Premium</p>
                      </div>
                      <div class="col-md-3 col-6 text-center">
                        <h5 class="text-success">ETB {{ formatAmount(paymentSummary.total_paid_for_year) }}</h5>
                        <p class="text-muted mb-0">Total Paid</p>
                      </div>
                      <div class="col-md-3 col-6 text-center">
                        <h5 class="text-info">{{ paymentSummary.payment_count }}</h5>
                        <p class="text-muted mb-0">Payments Made</p>
                      </div>
                      <div class="col-md-3 col-6 text-center">
                        <h5 :class="paymentSummary.balance_due > 0 ? 'text-warning' : 'text-success'">
                          ETB {{ formatAmount(paymentSummary.balance_due) }}
                        </h5>
                        <p class="text-muted mb-0">Balance Due</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Member Payment History -->
            <div v-if="paymentHistory.length > 0" class="card mb-4 border">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="bx bx-history me-1"></i>Member Payment History</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Date</th>
                      <th>Receipt/Reference</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Agency Status</th>
                      <th>Year</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="history in paymentHistory"
                        :key="history.payment_id"
                        :class="{'table-active': history.payment_id === paymentData.payment_id}">
                      <td>{{ formatDate(history.payment_date) }}</td>
                      <td>
                        <div>
                          {{ history.receipt_number || history.payment_reference }}
                          <div v-if="history.agency_banking_reference" class="small text-success">
                            <i class="bx bx-transfer me-1"></i>{{ history.agency_banking_reference }}
                          </div>
                        </div>
                      </td>
                      <td>ETB {{ formatAmount(history.amount) }}</td>
                      <td>{{ history.method_name }}</td>
                      <td>
                        <span :class="getStatusBadge(history.status)">
                          {{ capitalizeStatus(history.status) }}
                        </span>
                      </td>
                      <td>
                        <span v-if="history.method_name === 'Cash'"
                              :class="getAgencyBankingStatusBadge(history.agency_banking_status)">
                          {{ capitalizeStatus(history.agency_banking_status || 'not_sent') }}
                        </span>
                        <span v-else class="text-muted">N/A</span>
                      </td>
                      <td>{{ history.payment_year }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- No Data State -->
          <div v-else class="text-center py-5">
            <i class="bx bx-info-circle text-muted" style="font-size: 48px;"></i>
            <p class="mt-3 text-muted">No payment details available</p>
          </div>
        </div>

        <div class="modal-footer no-print">
          <button type="button" class="btn btn-outline-primary" @click="printReceipt">
            <i class="bx bx-printer me-1"></i>Print Receipt
          </button>
          <button type="button" class="btn btn-secondary" @click="closeModal">
            <i class="bx bx-x me-1"></i>Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentDetailsModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    payment: {
      type: Object,
      default: null
    },
    paymentHistory: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      retryLoading: false
    };
  },

  computed: {
    paymentData() {
      // Handle both nested data structure and direct payment object
      if (this.payment) {
        // If payment has a 'payment' property (nested structure from API)
        if (this.payment.payment) {
          return this.payment.payment;
        }
        // Direct payment object
        return this.payment;
      }
      return null;
    },

    planData() {
      return this.payment?.plan || null;
    },

    paymentSummary() {
      return this.payment?.payment_summary || null;
    },

    memberData() {
      return this.payment?.member || null;
    }
  },

  methods: {
    closeModal() {
      this.$emit('close');
    },

    printReceipt() {
      // Generate receipt HTML
      const receiptHTML = this.generateReceiptHTML();

      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=900');

      // Write the content to the new window
      printWindow.document.write(receiptHTML);
      printWindow.document.close();

      // Trigger print after content loads
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = function() {
            printWindow.close();
          };
        }, 250);
      };
    },

    generateReceiptHTML() {
      const styles = this.getReceiptStyles();
      const content = this.getReceiptContent();

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>CBHI Payment Receipt - ${this.paymentData?.receipt_number || this.paymentData?.payment_reference || 'N/A'}</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="receipt-container">
            ${content}
          </div>
        </body>
        </html>
      `;
    },

    getReceiptStyles() {
      return `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11px;
          line-height: 1.3;
          color: #333;
          background: white;
        }

        .receipt-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 15px;
          background: white;
        }

        .receipt-header {
          border-bottom: 2px solid #333;
          padding-bottom: 10px;
          margin-bottom: 12px;
          text-align: center;
        }

        .organization-logo h2 {
          font-size: 16px;
          color: #0056b3;
          margin-bottom: 3px;
        }

        .organization-logo h3 {
          font-size: 14px;
          color: #333;
          margin-bottom: 3px;
        }

        .organization-logo p {
          font-size: 11px;
          color: #666;
        }

        .receipt-info-bar {
          margin-top: 10px;
          padding: 8px;
          background-color: #f8f9fa;
          border-radius: 4px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0;
        }

        .two-column-layout {
          display: flex;
          justify-content: space-between;
          gap: 20px;
        }

        .column {
          flex: 1;
        }

        .receipt-section {
          margin-bottom: 12px;
        }

        .section-title {
          font-size: 11px;
          font-weight: bold;
          color: #0056b3;
          border-bottom: 1px solid #ddd;
          padding-bottom: 3px;
          margin-bottom: 6px;
          text-transform: uppercase;
        }

        .section-content {
          padding-left: 8px;
        }

        .receipt-table {
          width: 100%;
          border-collapse: collapse;
        }

        .receipt-table tr {
          border-bottom: 1px dotted #eee;
        }

        .receipt-table tr:last-child {
          border-bottom: none;
        }

        .label-cell {
          width: 45%;
          padding: 3px 0;
          font-weight: 600;
          color: #555;
          vertical-align: top;
          font-size: 10px;
        }

        .value-cell {
          width: 55%;
          padding: 3px 0;
          color: #333;
          vertical-align: top;
          font-size: 10px;
        }

        .amount-section {
          background-color: #f8f9fa;
          padding: 10px;
          border: 2px solid #28a745;
          border-radius: 5px;
          margin: 12px 0;
        }

        .amount-box {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .amount-label {
          font-size: 12px;
          color: #333;
          font-weight: bold;
        }

        .amount-value {
          font-size: 20px;
          color: #28a745;
          font-weight: bold;
        }

        .receipt-footer {
          margin-top: 15px;
          border-top: 2px solid #333;
          padding-top: 12px;
        }

        .signature-section {
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
        }

        .signature-box {
          width: 45%;
          text-align: center;
          padding: 5px;
        }

        .signature-line {
          border-bottom: 1px solid #333;
          margin-bottom: 3px;
          height: 30px;
        }

        .signature-label {
          font-size: 10px;
          color: #666;
          text-transform: uppercase;
        }

        .footer-notes {
          text-align: center;
          margin: 10px 0;
        }

        .footer-notes p {
          font-size: 10px;
          color: #666;
          margin-bottom: 2px;
        }

        .print-info {
          text-align: center;
          margin-top: 5px;
        }

        .print-info p {
          font-size: 9px;
          color: #999;
        }

        .text-danger {
          color: #dc3545;
          font-weight: bold;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }

          .receipt-container {
            max-width: 100%;
            padding: 10mm;
          }

          .amount-section {
            break-inside: avoid;
          }

          .receipt-section {
            break-inside: avoid;
          }

          .signature-section {
            break-inside: avoid;
          }

          @page {
            margin: 5mm;
            size: A4;
          }
        }
      `;
    },

    getReceiptContent() {
      const payment = this.paymentData;
      const plan = this.planData;
      const summary = this.paymentSummary;

      return `
        <!-- Receipt Header -->
        <div class="receipt-header">
          <div class="organization-logo">
            <h2>COMMUNITY BASED HEALTH INSURANCE</h2>
            <h3>PAYMENT RECEIPT</h3>
            <p>${payment?.zone_name || 'N/A'} Zone, ${payment?.woreda_name || 'N/A'} Woreda</p>
          </div>

          <div class="receipt-info-bar">
            <div class="info-row">
              <div><strong>Receipt No:</strong> ${payment?.receipt_number || payment?.payment_reference || 'N/A'}</div>
              <div><strong>Date:</strong> ${this.formatDate(payment?.payment_date)}</div>
            </div>
          </div>
        </div>

        <!-- Two Column Layout for Top Sections -->
        <div class="two-column-layout">
          <!-- Left Column -->
          <div class="column">
            <!-- Member Information -->
            <div class="receipt-section">
              <h5 class="section-title">MEMBER INFORMATION</h5>
              <div class="section-content">
                <table class="receipt-table">
                  <tr>
                    <td class="label-cell">Full Name:</td>
                    <td class="value-cell">${payment?.first_name || 'N/A'} ${payment?.surname || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">CBHI Number:</td>
                    <td class="value-cell">${payment?.cbhi_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Address:</td>
                    <td class="value-cell">${payment?.kebele_name || 'N/A'}, ${payment?.woreda_name || 'N/A'}, ${payment?.zone_name || 'N/A'}</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Payment Details -->
            <div class="receipt-section">
              <h5 class="section-title">PAYMENT DETAILS</h5>
              <div class="section-content">
                <table class="receipt-table">
                  <tr>
                    <td class="label-cell">Payment Year:</td>
                    <td class="value-cell">${payment?.payment_year || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Payment Method:</td>
                    <td class="value-cell">${payment?.method_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Payment Ref:</td>
                    <td class="value-cell">${payment?.payment_reference || 'N/A'}</td>
                  </tr>
                  ${payment?.bank_reference ? `
                  <tr>
                    <td class="label-cell">Bank Ref:</td>
                    <td class="value-cell">${payment.bank_reference}</td>
                  </tr>
                  ` : ''}
                  ${payment?.agency_banking_reference ? `
                  <tr>
                    <td class="label-cell">Agency Ref:</td>
                    <td class="value-cell">${payment.agency_banking_reference}</td>
                  </tr>
                  ` : ''}
                </table>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="column">
            <!-- Plan Information -->
            ${plan ? `
            <div class="receipt-section">
              <h5 class="section-title">INSURANCE PLAN</h5>
              <div class="section-content">
                <table class="receipt-table">
                  <tr>
                    <td class="label-cell">Plan Name:</td>
                    <td class="value-cell">${plan.plan_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Plan Code:</td>
                    <td class="value-cell">${plan.plan_code || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Coverage Limit:</td>
                    <td class="value-cell">ETB ${this.formatAmount(plan.annual_coverage_limit)}</td>
                  </tr>
                </table>
              </div>
            </div>
            ` : ''}

            <!-- Collection Details -->
            <div class="receipt-section">
              <h5 class="section-title">COLLECTION DETAILS</h5>
              <div class="section-content">
                <table class="receipt-table">
                  <tr>
                    <td class="label-cell">Collected By:</td>
                    <td class="value-cell">${payment?.collected_by || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Collection Point:</td>
                    <td class="value-cell">${payment?.collection_point || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Transaction Time:</td>
                    <td class="value-cell">${this.formatDateTime(payment?.created_at)}</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Amount Section -->
        <div class="amount-section">
          <div class="amount-box">
            <div class="amount-label">AMOUNT PAID</div>
            <div class="amount-value">ETB ${this.formatAmount(payment?.amount)}</div>
          </div>
        </div>

        <!-- Payment Summary -->
        ${summary ? `
        <div class="receipt-section">
          <h5 class="section-title">PAYMENT SUMMARY FOR ${payment?.payment_year || 'N/A'}</h5>
          <div class="section-content">
            <table class="receipt-table" style="width: 100%;">
              <tr>
                <td style="width: 25%; padding: 3px;"><strong>Expected Premium:</strong> ETB ${this.formatAmount(summary.expected_annual_premium)}</td>
                <td style="width: 25%; padding: 3px;"><strong>Total Paid:</strong> ETB ${this.formatAmount(summary.total_paid_for_year)}</td>
                <td style="width: 25%; padding: 3px;"><strong>Payments:</strong> ${summary.payment_count || 0}</td>
                <td style="width: 25%; padding: 3px;"><strong>Balance:</strong> <span class="${summary.balance_due > 0 ? 'text-danger' : ''}">ETB ${this.formatAmount(summary.balance_due)}</span></td>
              </tr>
            </table>
          </div>
        </div>
        ` : ''}

        <!-- Footer -->
        <div class="receipt-footer">
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="signature-label">Authorized Signature</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <p class="signature-label">Member Signature</p>
            </div>
          </div>

          <div class="footer-notes">
            <p><strong>Thank you for your payment!</strong></p>
            <p>Please keep this receipt for your records. For queries, contact your local CBHI office.</p>
          </div>

          <div class="print-info">
            <p>Printed on: ${this.getCurrentDateTime()}</p>
          </div>
        </div>
      `;
    },

    async retryAgencyBanking() {
      this.retryLoading = true;
      this.$emit('retryAgencyBanking', this.paymentData);

      // Reset loading state after a delay
      setTimeout(() => {
        this.retryLoading = false;
      }, 3000);
    },

    // Agency Banking Methods
    getAgencyBankingStatusBadge(status) {
      const badges = {
        'sent': 'badge bg-success-subtle text-success',
        'failed': 'badge bg-danger-subtle text-danger',
        'pending': 'badge bg-warning-subtle text-warning',
        'not_sent': 'badge bg-secondary-subtle text-secondary'
      };
      return badges[status] || 'badge bg-secondary-subtle text-secondary';
    },

    getAgencyBankingStatusIcon(status) {
      const icons = {
        'sent': 'bx bx-check-circle',
        'failed': 'bx bx-x-circle',
        'pending': 'bx bx-time-five',
        'not_sent': 'bx bx-circle'
      };
      return icons[status] || 'bx bx-circle';
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getCurrentDateTime() {
      return new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    getInitials(firstName, lastName) {
      return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
    },

    capitalizeStatus(status) {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    getStatusBadge(status) {
      const badges = {
        'completed': 'badge bg-success',
        'pending': 'badge bg-warning text-dark',
        'failed': 'badge bg-danger',
        'reversed': 'badge bg-secondary'
      };
      return badges[status] || 'badge bg-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'completed': 'bx bx-check-circle',
        'pending': 'bx bx-time-five',
        'failed': 'bx bx-x-circle',
        'reversed': 'bx bx-undo'
      };
      return icons[status] || 'bx bx-circle';
    },

    getPaymentMethodBadge(method) {
      const badges = {
        'Cash': 'badge bg-success',
        'Telebirr': 'badge bg-primary',
        'M-Pesa': 'badge bg-primary',
        'CBE Birr': 'badge bg-info',
        'Bank Transfer': 'badge bg-info',
        'Commercial Bank of Ethiopia': 'badge bg-info',
        'Abay Bank': 'badge bg-info',
        'Employer Credit': 'badge bg-warning text-dark',
        'Traffic Police': 'badge bg-danger',
        'Cooperative Union': 'badge bg-secondary',
        'MFI Payment': 'badge bg-dark'
      };
      return badges[method] || 'badge bg-secondary';
    }
  }
};
</script>

<style scoped>
.spinner-border {
  width: 2rem;
  height: 2rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.modal.show {
  display: block !important;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-sm .avatar-title {
  width: 2.5rem;
  height: 2.5rem;
  font-size: 12px;
}

.table-active {
  background-color: rgba(0, 123, 255, 0.1);
}

.payment-detail-card {
  background-color: rgba(25, 135, 84, 0.1);
}

.badge {
  font-size: 0.75em;
}

.text-dark {
  color: #495057 !important;
}

.bg-success-subtle {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }

  .modal-dialog {
    margin: 0.5rem;
  }

  .avatar-sm {
    width: 2rem;
    height: 2rem;
  }

  .avatar-sm .avatar-title {
    width: 2rem;
    height: 2rem;
    font-size: 10px;
  }
}
</style>